
import { defineComponent, computed, onMounted, ref, onUnmounted } from "vue";
import { 
  FridgeDetailsPushSettings,
  FridgeDetailsFunctionList,
  FridgeDetailsDisplaySettingsListItem,
  FRIDGE_DETAILS_PUSH_SETTINGS
} from "@/types/fridgeDetails";

import NativeClient, { TitleInfo } from "@/client/native-client";
import store from "@/store";
import router from "@/router";
import { useApiErrorHandler } from "@/use/use-api-error-handler";
import { useApiErrorDialog } from "@/use/use-api-error-dialog";
import { useLog } from "@/use/use-log";
import { PAGE_ID } from "@/const/log-const";
import { ApiError, BeaconLogInfo, UpdatePageInfo } from "@/types/common";
import { COMMON_LIMIT_NUM } from "@/const/limit_const";
import { COMMON_MESSAGE_STRING } from "@/const/message_const";
import CommonUtil from "@/utils/common-utils";
import CommonDesignDialog from "@/components/common/CommonDesignDialog.vue";
import CommonSwitch from "@/components/common/CommonSwitch.vue";
import FridgeDetailsSelectHours from "@/components/fridgeDetails/FridgeDetailsSelectHours.vue";
import FridgeDetailsConfirmSettingsDialog from "@/components/fridgeDetails/FridgeDetailsConfirmSettingsDialog.vue";

export default defineComponent({
  name: "FridgePushSettingsPage",
  components: { 
    CommonSwitch, 
    CommonDesignDialog,
    FridgeDetailsSelectHours,
    FridgeDetailsConfirmSettingsDialog
  },
  setup() {
    const { setLogId, outputPageLogs } = useLog();

    // タイトル行の設定
    NativeClient.setTitle(new TitleInfo(false, "通知設定"));
    CommonUtil.scroll({ top: 0, left: 0 });
    NativeClient.sendDebugLog("FridgePushSettingsPage setup() rout = " + JSON.stringify(router.options.history));

    // クリア
    store.dispatch("fridgeDetails/updatePushSettingsBuffer", null);

    // 表示更新のためのリアクティブ
    const updateDisplayKey = ref(0);
    const updateDisplay = () => {
      if (updateDisplayKey.value == 100) {
        updateDisplayKey.value = 0;
      } else {
        updateDisplayKey.value++;
      }
    };

    // 文言
    const errorOccuredText = "エラー発生";
    const errorOccuredDescriptionText = "冷蔵庫のエラー発生時にお知らせします。";
    const coolingAssistText = "クーリングアシスト";
    const coolingAssistDescriptionText = "クーリングアシスト終了時、取り消し時、変更時にお知らせします。";
    const waterShortageText = "給水不足";
    const waterShortageDescriptionText = "給水タンクの水がなくなった場合にお知らせします。";
    const iceMakingCompletionText = "製氷完了";
    const iceMakingCompletionDescriptionText = "製氷完了時にお知らせします。";
    const doorMonitorText = "ドアモニター";
    const doorMonitorDescriptionText = "設定時間内にドア開閉がない場合にお知らせします。";
    const sendButtonText = "送信";
    const doorMonitorTimeButtonText = "変更";
    const partialHalfTitleText = "パーシャル半解凍";
    const partialHalfDescriptionText = "パーシャル半解凍開始時、終了時、取り消し時にお知らせします。";
    const completeDialogTitle = "設定完了しました。";
    const completeDialogNegativeButtonTitle = "閉じる";

    // ダイアログ
    class FridgePushSettingsPageDialog {
      positiveButtonHandler: (() => void) | null;
      negativeButtonHandler: (() => void) | null;

      constructor() {
        this.positiveButtonHandler = null;
        this.negativeButtonHandler = null;
      }
    }
    let dialogInfo = new FridgePushSettingsPageDialog();
    const isShowDialog = ref(false);
    const dialogType = ref(0);
    const dialogTitle = ref("");
    const dialogMessage = ref("");
    const dialogPositiveButtonTitle = ref("");
    const dialogNegativeButtonTitle = ref("");
    const dialogPositiveButtonHandler = () => {
      isShowDialog.value = false;
      if (dialogInfo.positiveButtonHandler != null) {
        dialogInfo.positiveButtonHandler();
      }
    };
    const dialogNegativeButtonHandler = () => {
      isShowDialog.value = false;
      if (dialogInfo.negativeButtonHandler != null) {
        dialogInfo.negativeButtonHandler();
      }
    };

    // 設定完了ダイアログ
    const completeSettings = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage completeSettings()");
      // 遷移元の画面に戻る
      if (router.options.history.state.back == null) {
        // 本画面がルートならば、WebViewを閉じるように、Nativeに要求する
        NativeClient.sendDebugLog("FridgePushSettingsPage completeSettings() for Page Close request to Native");
        NativeClient.pageClose();
      } else {
        NativeClient.sendDebugLog("FridgePushSettingsPage completeSettings() for Goback");
        router.go(-1);
      }
    };

    const showCompleteDialog = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage showCompleteDialog()");
      dialogType.value = 12;
      dialogTitle.value = completeDialogTitle;
      dialogMessage.value = "";
      dialogPositiveButtonTitle.value = "";
      dialogNegativeButtonTitle.value = completeDialogNegativeButtonTitle;
      dialogInfo = new FridgePushSettingsPageDialog();
      dialogInfo.negativeButtonHandler = completeSettings;
      isShowDialog.value = true;
    };

    // 機能の有効無効判定
    const checkFunction = (functionId: string, isCheckPushSettings: boolean) => {
      const settingsBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      const functionList: FridgeDetailsFunctionList | null = store.getters["fridgeDetails/getFunctionList"];
      if (settingsBuffer == null || functionList == null) {
        NativeClient.sendDebugLog("FridgePushSettingsPage checkFunction data is null = " + functionId);
        return false;
      }
      const indexPushSettings = settingsBuffer.param_list.findIndex(element => element.param_name == functionId);
      const indexFunctionList = functionList.reizo_function_list.findIndex(element => element.function_id == functionId);
      if ((isCheckPushSettings && indexPushSettings == -1) || indexFunctionList == -1) {
        NativeClient.sendDebugLog("FridgePushSettingsPage checkFunction data is not exist = " + functionId);
        return false;
      }
      if (functionList.reizo_function_list[indexFunctionList].function_value) {
        NativeClient.sendDebugLog("FridgePushSettingsPage checkFunction data is valid = " + functionId);
        return true;
      }
      return false;
    };

    // 通知設定の取得
    const getIndexOfSettings = (settings: FridgeDetailsPushSettings, id: string) => {
      return settings.param_list.findIndex(element => element.param_name == id);
    };
    const getOnOffValue = (id: string) => {
      const settingsBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      NativeClient.sendDebugLog("FridgePushSettingsPage getOnOffValue id = " + id + ", settings = " + JSON.stringify(settingsBuffer));
      if (settingsBuffer == null) {
        return false;
      }
      const index = getIndexOfSettings(settingsBuffer, id);
      if (index == -1) {
        return false;
      }
      return settingsBuffer.param_list[index].param_value;
    };
    const getTimeValue = (id: string) => {
      const settingsBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingsBuffer == null) {
        return null;
      }
      const index = getIndexOfSettings(settingsBuffer, id);
      if (index == -1) {
        return null;
      }
      return settingsBuffer.param_list[index].param_time;
    };

    // 表示／非表示
    const isValidSettings = ref(false);
    const isValidCoolingAssist = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.COOLOVEN, true)) {
        return true;
      }
      return false;
    });
    const isValidDoorMonitor = computed(() =>{
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO, true)) {
        return true;
      }
      return false;
    });
    const isValidDoorMonitorTime = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO, true)) {
        if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO)) {
          return true;
        }
      }
      return false;
    });
    const isValidPartialHalf = computed(() => {
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.PARTIALHALFDEFROST, true)) {
        return true;
      }
      return false;
    });

    // On/Off設定値取得
    const errorOccuredStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.ERROROCCURED);
    });
    const coolingAssistStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.COOLOVEN);
    });
    const waterShortageStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.WATERSHORTAGE);
    });
    const iceMakingCompletionStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.ICECOMPLETED);
    });
    const doorMonitorStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO);
    });
    const partialHalfStatus = computed(() => {
      return getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.PARTIALHALFDEFROST);
    });

    //
    // ドアモニター関連
    //
    // ドアモニター通知時間
    const doorMonitorTimeText = computed(() => {
      const settingBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingBuffer != null) {
        NativeClient.sendDebugLog("FridgePushSettingsPage doorMonitorTimeText() settingBuffer exist");
        const index = getIndexOfSettings(settingBuffer, FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO);
        if (index != -1) {
          NativeClient.sendDebugLog("FridgePushSettingsPage doorMonitorTimeText() settingBuffer index exist");
          
          if (settingBuffer.param_list[index].param_time == undefined || settingBuffer.param_list[index].param_time == null) {
            // ドアモニターOFFの場合、この値は、undefinedになっているはなので、１時間を設定する
            return "1時間";
          }
          return `${settingBuffer.param_list[index].param_time}時間`;
        }
      }
      return "";
    });
    // 時間のピッカー
    const isShowSelectHours = ref(false);
    const minHour = 1;
    const maxHour = 72;
    const initialHour = computed(() => {
      const settingBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingBuffer == null) {
        NativeClient.sendDebugLog("FridgePushSettingsPage doorMonitorTimeText() initialHour settingBuffer not exist");
        return 1;
      }
      const index = settingBuffer.param_list.findIndex(element => (element.param_name == FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO));
      if (index == -1) {
        NativeClient.sendDebugLog("FridgePushSettingsPage doorMonitorTimeText() initialHour doorOpenInfo not exist");
        return 1;
      }
      if (settingBuffer.param_list[index].param_time == undefined || settingBuffer.param_list[index].param_time == null) {
        // ドアモニターOFFの場合、この値は、undefinedになっているはなので、１時間を設定する
        return 1;
      }
      return settingBuffer.param_list[index].param_time;
    });
    const selectHours = (hour: number) => {
      NativeClient.sendDebugLog("FridgePushSettingsPage selectHours() hour = " + hour);
      isShowSelectHours.value = false;
      const settingBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingBuffer == null) {
        return;
      }
      const index = settingBuffer.param_list.findIndex(element => (element.param_name == FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO));
      if (index == -1) {
        return;
      }
      settingBuffer.param_list[index].param_time = hour;
      NativeClient.sendDebugLog("FridgePushSettingsPage selectHours() updatePushSettingsBuffer");
      store.dispatch("fridgeDetails/updatePushSettingsBuffer", settingBuffer);
    };
    // キャンセル
    const cancelSelectHours = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage cancelSelectHours()");
      isShowSelectHours.value = false;
    }
    // 変更ボタン
    const clickChangeTime = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage clickChangeTime()");
      updateDisplay();
      isShowSelectHours.value = true;
    };

    // スイッチ設定
    const setParamOnOff = (id: string, value: boolean) => {
      const settingBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingBuffer != null) {
        const index = getIndexOfSettings(settingBuffer, id);
        if (index != -1) {
          settingBuffer.param_list[index].param_value = value;
          NativeClient.sendDebugLog("FridgePushSettingsPage setParamOnOff() updatePushSettingsBuffer");
          store.dispatch("fridgeDetails/updatePushSettingsBuffer", settingBuffer);
        }
      }
    };
    const changeErrorOccured = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.ERROROCCURED, isOn);
    };
    const changeCoolingAssist = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.COOLOVEN, isOn);
    };
    const changeWaterShortage = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.WATERSHORTAGE, isOn);
    };
    const changeIceMakingCompletion = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.ICECOMPLETED, isOn);
    };
    const changeDoorMonitor = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO, isOn);
      const settingsBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingsBuffer != null) {
        const index = getIndexOfSettings(settingsBuffer, FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO);
        if (index != -1) {
          if (settingsBuffer.param_list[index].param_value && settingsBuffer.param_list[index].param_time == undefined) {
            settingsBuffer.param_list[index].param_time = 1;
          }
        }
      }
    };
    const changePartialHalfValueCompletion = (isOn: boolean) => {
      setParamOnOff(FRIDGE_DETAILS_PUSH_SETTINGS.PARTIALHALFDEFROST, isOn);
    };

    // 最後の説明文
    const bottomDescriptionText = computed(() => {
      let retText = "";
      if (checkFunction("houseSitting", false)) {
        retText = "「お留守番モード」、「お買い物準備モード」、「冬季省エネ運転」では、PUSH通知のON/OFFは選択できません。";
      } else {
        retText = "「お買い物準備モード」、「冬季省エネ運転」では、PUSH通知のON/OFFは選択できません。";
      }
      return retText;
    });

    //
    // API実行
    //
    // API実行状態
    class FridgePushSettingsPageAPIExecutionStatus {
      type: number; // 0:ダイアログ表示なし、1:初期データ取得実行中、2:設定登録実行中
      constructor(type: number) {
        this.type = type;
      }
    }
    const apiStatus = ref(new FridgePushSettingsPageAPIExecutionStatus(0));
    const getData = async () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage getData()");
      store.dispatch("common/startLoading");
      apiStatus.value = new FridgePushSettingsPageAPIExecutionStatus(1);
      await NativeClient.getApiParams();
      await Promise.all([
        // 冷蔵庫サーバーの実装次第
        store.dispatch("fridgeDetails/getPushSettings"),
        store.dispatch("fridgeDetails/getFunctionList")
      ]);
      store.dispatch("common/stopLoading");
      apiStatus.value = new FridgePushSettingsPageAPIExecutionStatus(0);

      const result = store.getters["fridgeDetails/getPushSettings"];
      NativeClient.sendDebugLog("FridgePushSettingsPage getData() result = " + JSON.stringify(result));
      if (result != null) {
        NativeClient.sendDebugLog("FridgePushSettingsPage getData() result valid");
        NativeClient.sendDebugLog("FridgePushSettingsPage getData() updatePushSettingsBuffer");
        store.dispatch("fridgeDetails/updatePushSettingsBuffer", result);
        isValidSettings.value = true;
      }
      updateDisplay();
    };
    const registData = async () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage registData()");
      const settingBuffer: FridgeDetailsPushSettings | null = store.getters["fridgeDetails/getPushSettingsBuffer"];
      if (settingBuffer != null) {
        // ドアモニター以外の項目の時間を削除、また、ドアモニターがOFFの場合、時間を削除
        for (let index = 0; index < settingBuffer.param_list.length; index ++) {
          if (settingBuffer.param_list[index].param_name != FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO || settingBuffer.param_list[index].param_value == false) {
            delete settingBuffer.param_list[index].param_time;
          }
        }
        apiStatus.value = new FridgePushSettingsPageAPIExecutionStatus(2);
        // 冷蔵庫サーバーの実装次第
        store.dispatch("common/startLoading");
        await store.dispatch("fridgeDetails/registPushSettings", settingBuffer),
        store.dispatch("common/stopLoading");
        apiStatus.value = new FridgePushSettingsPageAPIExecutionStatus(0);

        NativeClient.sendDebugLog("FridgePushSettingsPage registData() api executed!!");

        // 完了ダイアログを表示する
        showCompleteDialog();
      }
    };

    //
    // ページの表示
    //
    onMounted(() => {
      NativeClient.sendDebugLog("FridgePushSettingsPage onMounted()");

      // ページIDは未決定
      // const logInfo = new BeaconLogInfo(PAGE_ID.FAVORITES_MYLIST, null, []);
      // NativeClient.sendViewedLog(logInfo);

      getData();
    });
    onUnmounted(() => {
      NativeClient.sendDebugLog("FridgePushSettingsPage onUnmounted()");
    });

    //
    // 送信処理
    //
    const isShowConfirm = ref(false);
    const confirmDialogTitle = "通知設定確認";
    const confirmList = computed(() => {
      const result: Array<FridgeDetailsDisplaySettingsListItem> = [];
      let index = 0;
      let item: FridgeDetailsDisplaySettingsListItem = { index: index, itemName: "", value: ""};
      let value = "オフ";
      
      // エラー発生
      if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.ERROROCCURED)) {
        value = "オン";
      }
      item = { index: index, itemName: "エラー発生", value: value }
      result.push(item);

      // クーリングアシスト
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.COOLOVEN, true)) {
        let value = "オフ";
        if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.COOLOVEN)) {
          value = "オン";
        }
        const item: FridgeDetailsDisplaySettingsListItem = { index: index, itemName: "クーリングアシスト", value: value };
        result.push(item);
        index ++;
      }

      // 給水不足
      value = "オフ";
      if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.WATERSHORTAGE)) {
        value = "オン";
      }
      item = { index: index, itemName: "給水不足", value: value };
      result.push(item);
      index ++;

      // 製氷完了
      value = "オフ";
      if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.ICECOMPLETED)) {
        value = "オン";
      }
      item = { index: index, itemName: "製氷完了", value: value };
      result.push(item);
      index ++;

      // ドア開閉なし
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO, true)) {
        value = "オフ";
        if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO)) {
          value = "オン";
        }
        item = { index: index, itemName: "ドア開閉なし", value: value };
        result.push(item);
        index ++;

        if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO)) {
          const param = getTimeValue(FRIDGE_DETAILS_PUSH_SETTINGS.DOOROPENINFO);
          if (param != null) {
            item = { index: index, itemName: "ドア開閉なし時間", value: `${param}時間` };
            result.push(item);
            index ++;
          }
        }
      }

      // パーシャル半解凍
      if (checkFunction(FRIDGE_DETAILS_PUSH_SETTINGS.PARTIALHALFDEFROST, true)) {
        value = "オフ";
        if (getOnOffValue(FRIDGE_DETAILS_PUSH_SETTINGS.PARTIALHALFDEFROST)) {
          value = "オン";
        }
        item = { index: index, itemName: "パーシャル半解凍", value: value };
        result.push(item);
      }

      return result;
    });

    // 送信ボタン
    const sendButtonAction = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage sendButtonAction()");
      isShowConfirm.value = true;
    };
    const cancelClick = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage cancelClick()");
      isShowConfirm.value = false;
    };
    const sendClick = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage sendClick()");
      isShowConfirm.value = false;
      registData();
    };


    //
    // APIリクエスト時のエラーハンドル
    //
    const {
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage
    } = useApiErrorHandler();
    const { closeDialog } = useApiErrorDialog();
    const typeApiError = ref(9);
    const retryButtonTitle = "リトライ";
    const okButtonTitle = "OK";
    // APIのエラー・ダイアログは、リトライが存在しないが、共通ダイアログのIFは残っているため、削除せず、そのまま置いておく
    const retryApiErrorHandler = () => {
      NativeClient.sendDebugLog("FridgePushSettingsPage retryApiErrorHandler()");
      closeDialog();
      // if (apiStatus.value.type == 1) {
      //   NativeClient.sendDebugLog("FridgePushSettingsPage retryApiErrorHandler() getData()");
      //   getData();
      // } else  if (apiStatus.value.type == 2) {
      //   NativeClient.sendDebugLog("FridgePushSettingsPage retryApiErrorHandler() registData()");
      //   registData();
      // } else {
      //   NativeClient.sendDebugLog("FridgePushSettingsPage retryApiErrorHandler() no valid");
      // }
    };
    const okApiErrorHandler = () => {
      closeDialog();
    };

    return {
      isValidSettings,
      isValidCoolingAssist,
      isValidDoorMonitor,
      isValidDoorMonitorTime,
      isValidPartialHalf,
      errorOccuredText,
      errorOccuredDescriptionText,
      coolingAssistText,
      coolingAssistDescriptionText,
      waterShortageText,
      waterShortageDescriptionText,
      iceMakingCompletionText,
      iceMakingCompletionDescriptionText,
      doorMonitorText,
      doorMonitorDescriptionText,
      doorMonitorTimeText,
      doorMonitorTimeButtonText,
      partialHalfTitleText,
      partialHalfDescriptionText,
      clickChangeTime,
      sendButtonText,
      errorOccuredStatus,
      coolingAssistStatus,
      waterShortageStatus,
      iceMakingCompletionStatus,
      doorMonitorStatus,
      partialHalfStatus,
      bottomDescriptionText,
      sendButtonAction,
      changeErrorOccured,
      changeCoolingAssist,
      changeWaterShortage,
      changeIceMakingCompletion,
      changeDoorMonitor,
      changePartialHalfValueCompletion,
      updateDisplayKey,
      isShowSelectHours,
      minHour,
      maxHour,
      initialHour,
      selectHours,
      cancelSelectHours,
      isShowConfirm,
      confirmDialogTitle,
      confirmList,
      cancelClick,
      sendClick,
      handleApiError,
      getApiErrorTitle,
      getApiErrorMessage,
      typeApiError,
      retryButtonTitle,
      okButtonTitle,
      retryApiErrorHandler,
      okApiErrorHandler,
      isShowDialog,
      dialogType,
      dialogTitle,
      dialogMessage,
      dialogPositiveButtonTitle,
      dialogNegativeButtonTitle,
      dialogPositiveButtonHandler,
      dialogNegativeButtonHandler
    };
  }
});
